/**
 * Progress sync orchestrator · per OAQ-2b-5=C hybrid pattern.
 *
 * Responsibilities (FS slot · post-fix 2026-05-31):
 *  1. Receive `Progress` snapshots from `<ProgressProvider>` via `useProgressSync`.
 *  2. Debounce per-branch (default 200ms) so a burst of edits within the
 *     window collapses into a single API patch.
 *  3. Dispatch the right `PATCH /progress/<branch>` call · attach per-branch
 *     `If-Unmodified-Since` from `branchTimestamps` tracker.
 *  4. On 200: capture `meta.lastModified` from the **server response envelope**
 *     and store as the branch's new tracker value (server clock truth · no
 *     client-clock drift).
 *  5. On 412 SyncConflictError: refetch `/progress` (full GET), call
 *     `onRefetch` so React applies the fresh state, then retry the patch
 *     once. Second 412 → emit `'persistent'` conflict.
 *  6. On networkError: emit `'offline'` (rate-limited 30s per FS.1 design)
 *     and leave the queue intact; on next mutation the patch re-fires.
 *  7. `initialFetch()` runs once after auth · seeds the per-branch tracker
 *     from the row's `Last-Modified` header and pushes the server state via
 *     `onRefetch`.
 *
 * Bypass note: this module dispatches PATCHes via `apiFetchEnvelope` directly
 * (not through `api.progress.patchXxx` wrappers) because the wrappers discard
 * response meta. The wrappers remain for UI-layer callers that only need the
 * body. Schemas here are permissive — the orchestrator only cares about the
 * 2xx + headers, not the echoed body.
 */

import { z } from 'zod';
import type { Progress } from '@shared/schemas';
import { ProgressSchema } from '@shared/schemas';
import { apiFetchEnvelope, ApiClientError, SyncConflictError } from '@/api';
import { supabase } from '@/api/supabaseClient';
import { trackSyncStorm } from '@/lib/analytics/track';
import type { SyncConflictKind } from '@/components/toasts';

type BranchKey =
  | 'last'
  | 'tickets'
  | 'sprints'
  | 'homework'
  | 'onboarding'
  | 'pomodoro'
  | 'cockpit'
  | 'achievements'
  | 'capstone'
  // slot_board_overview P3.2 (9 JUL): event-log stream. Unlike the other
  // full-replace branches, its server patcher APPENDS + dedupes-by-id + prunes
  // 90d (idempotent), so re-sending the same batch is a no-op — the silent 412
  // refetch+retry path stays safe without special-casing here.
  | 'events';

const ALL_BRANCHES: ReadonlyArray<BranchKey> = [
  'last',
  'tickets',
  'sprints',
  'homework',
  'onboarding',
  'pomodoro',
  'cockpit',
  'achievements',
  'capstone',
  'events',
];

interface SyncQueueItem {
  branch: BranchKey;
  /** For per-id branches (tickets/sprints/homework/cockpit-flashcards) the id. */
  itemId?: string;
  /**
   * Live-value getter (ADR-019 · P2). Read at SEND time, not schedule time, so a
   * 412 retry always ships the CURRENT local value instead of a snapshot captured
   * when the item was queued. This closes the data-corruption vector that rolled
   * `S04.status` completed→available: an item dequeued into `current` no longer
   * carries a stale copy that a slow retry could land last, over a fresher write.
   */
  read: () => unknown;
  /** Number of retries already attempted. */
  retries: number;
}

export interface ProgressSyncEvents {
  /** Fired when a 412 conflict was auto-resolved or escalated to persistent. */
  onConflict: (kind: SyncConflictKind) => void;
  /** Fired when authed state requires reseeding `progress` from server. */
  onRefetch: (next: Progress) => void;
}

const DEBOUNCE_MS = 200;
/**
 * Silent conflict recovery (slot · 21 JUN · Genie hard rule «никаких sync-ошибок
 * пользователю, даже с 50 вкладками»). On 412 we refetch the latest version,
 * MERGE our pending writes on top (so optimistic UI — e.g. a tutorial dismiss —
 * never reverts), and retry. We keep retrying up to MAX_CONFLICT_RETRIES with a
 * small backoff; conflicts between concurrent sessions are transient, so the write
 * lands well within this budget. We NEVER surface an 'auto-resolved' / 'persistent'
 * toast — recovery is invisible. Only a genuine networkError still informs ('offline').
 */
const MAX_CONFLICT_RETRIES = 8;
const CONFLICT_BACKOFF_MS = 60;
const CONFLICT_BACKOFF_CAP_MS = 360;
/** `'offline'` toast cooldown per FS.1 design — avoid spamming during a long offline session. */
const OFFLINE_TOAST_COOLDOWN_MS = 30_000;
/**
 * `'auto-resolved'` toast cooldown — concurrent editing in two sessions of the
 * same user ping-pongs 412→refetch→retry. Each cycle is silent recovery, but
 * the toast every time is noise. One per ~5s is enough to inform the student
 * "something synced from your other device" without spam.
 */
const AUTO_RESOLVED_TOAST_COOLDOWN_MS = 5_000;

// ── Storm detector (ADR-019 · P4) ────────────────────────────────────────────
// Normal work generates single-digit PATCHes/min. A cascade (identity-diff of a
// re-parsed graph, or the cross-tab ping-pong) fires ~40 in a burst. If the sent
// rate crosses the threshold within a rolling minute, record ONE analytics row
// (branch mix + count) and cool down so the detector never becomes a storm
// itself. NO toast — Genie hard rule: no sync errors to the user. Purely
// observability: the storm was previously invisible (only a console.warn).
const STORM_PATCH_THRESHOLD = 30;
const STORM_WINDOW_MS = 60_000;
const STORM_REPORT_COOLDOWN_MS = 5 * 60_000;

const ProgressGetResponse = z.object({ progress: ProgressSchema });
/** Permissive PATCH echo · we only need 2xx + Last-Modified header, not the body. */
const PatchEchoPassthrough = z.object({}).passthrough();

interface BranchRoute {
  /** URL relative to `/api/progress` (e.g., '/last', '/ticket/:id'). */
  buildPath: (itemId?: string) => string | null;
}

const BRANCH_ROUTES: Record<BranchKey, BranchRoute> = {
  last: { buildPath: () => '/api/progress/last' },
  tickets: {
    buildPath: (id) => (id ? `/api/progress/ticket/${encodeURIComponent(id)}` : null),
  },
  sprints: {
    buildPath: (id) => (id ? `/api/progress/sprint/${encodeURIComponent(id)}` : null),
  },
  homework: {
    buildPath: (id) => (id ? `/api/progress/homework/${encodeURIComponent(id)}` : null),
  },
  onboarding: { buildPath: () => '/api/progress/onboarding' },
  pomodoro: { buildPath: () => '/api/progress/pomodoro' },
  cockpit: {
    buildPath: (id) =>
      id
        ? `/api/progress/cockpit/flashcards/${encodeURIComponent(id)}`
        : '/api/progress/cockpit',
  },
  achievements: { buildPath: () => '/api/progress/achievements' },
  capstone: { buildPath: () => '/api/progress/capstone/block-1' },
  // Batch body `{ events: [...] }` (server caps at 500) · append-idempotent.
  events: { buildPath: () => '/api/progress/events' },
};

class ProgressSyncManager {
  private queue = new Map<string, SyncQueueItem>();
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private events: ProgressSyncEvents | null = null;
  private enabled = false;
  /** Per-branch `If-Unmodified-Since` tracker · server-truth (from response Last-Modified). */
  private branchTimestamps = new Map<BranchKey, string>();
  /**
   * GLOBAL dispatch chain (slot_sync_storm_fix · Client B) · serialize ALL
   * PATCHes across every branch so a fresh-login cascade fires them
   * one-at-a-time. Concurrent writes otherwise contend on the server's per-row
   * compare-and-swap (Server A), producing retry stampedes and edge wall-clock
   * 500s. Was previously per-branch (`inFlightPerBranch`), which still let
   * different branches race the same row.
   */
  private inFlightChain: Promise<void> = Promise.resolve();
  /**
   * Writes are gated until the initial GET /progress has SUCCEEDED. We never
   * PATCH over a server snapshot we couldn't confirm — a degraded fetch must
   * not let stale local state overwrite the server (the data-loss / cross-user
   * corruption vector · slot_sync_storm_fix · Client B).
   */
  private writeReady = false;
  /**
   * Branches the client has successfully PATCH'ed at least once in the
   * current session. The first PATCH per branch deliberately omits
   * `If-Unmodified-Since` — the server's `_meta.branches[X]` is `undefined`
   * for un-patched branches, falling back to `_meta.lastUpdatedAt`, which
   * advances on every PATCH (including from concurrent cascade writes in
   * this session). Sending a stale `If-Unmodified-Since` against that
   * fallback produces a false 412 on the second cascading PATCH (the
   * scenario behind the "persistent" toast after dev restarts). Once we've
   * patched a branch, `_meta.branches[X]` is set server-side and the
   * fallback is no longer used, so conflict detection works correctly.
   */
  private branchPatched = new Set<BranchKey>();
  private lastOfflineToastAt = 0;
  private lastAutoResolvedToastAt = 0;
  /** Storm detector (P4) · rolling window of sent PATCHes + last-report stamp. */
  private patchWindow: Array<{ at: number; branch: BranchKey }> = [];
  private lastStormReportAt = 0;
  /**
   * The user id this sync session belongs to. Set by `useProgressSync` on the
   * identity effect. Every PATCH re-checks the LIVE Supabase session against it
   * and aborts if they differ — a defence-in-depth guard for the multi-tab
   * account-swap race (shared Supabase session): even if a write was queued for
   * user A just before another tab logged in as B, we refuse to PATCH A's data
   * under B's token. Fail-safe: skipping a write never corrupts; the next
   * initialFetch/edit re-syncs.
   */
  private ownerId: string | null = null;

  /** Bind this sync session to a user id (defence-in-depth · multi-tab swap). */
  setOwner(userId: string | null): void {
    this.ownerId = userId;
  }

  setEvents(events: ProgressSyncEvents | null) {
    this.events = events;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    if (!enabled) {
      for (const t of this.timers.values()) clearTimeout(t);
      this.timers.clear();
      this.queue.clear();
      this.branchTimestamps.clear();
      this.inFlightChain = Promise.resolve();
      this.writeReady = false;
      this.branchPatched.clear();
      this.lastOfflineToastAt = 0;
      this.lastAutoResolvedToastAt = 0;
      this.patchWindow = [];
      this.lastStormReportAt = 0;
    }
  }

  /**
   * Initial GET /progress after auth · seeds the per-branch tracker and pushes
   * the fresh state to React via `onRefetch`. Failures are silent except for
   * the network case (toast 'offline').
   */
  async initialFetch(): Promise<void> {
    if (!this.enabled) return;
    try {
      const envelope = await apiFetchEnvelope(ProgressGetResponse, '/api/progress');
      this.seedTrackerFromLastModified(envelope.meta.lastModified);
      this.events?.onRefetch(envelope.data.progress);
      // Server truth confirmed · writes may now flow (Client B write-gating).
      this.writeReady = true;
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'networkError') {
        this.emitConflict('offline');
        return;
      }
      console.warn('[progress-sync] initialFetch failure', err);
    }
  }

  /** Manual retry · invoked by the `SyncConflictToast` persistent variant. */
  async retryAll(): Promise<void> {
    this.lastOfflineToastAt = 0;
    await this.initialFetch();
  }

  /**
   * Schedule a debounced sync of a branch. `read` is a getter for the branch's
   * CURRENT value (ADR-019 · P2) — the orchestrator calls it at send time, so a
   * debounce-collapsed burst or a 412 retry always ships the freshest value.
   */
  schedule(branch: BranchKey, read: () => unknown, itemId?: string): void {
    // Gate on both enabled (authed) and writeReady (initial GET confirmed) so a
    // degraded/failed initialFetch never lets stale local state PATCH over the
    // server (slot_sync_storm_fix · Client B).
    if (!this.enabled || !this.writeReady) return;
    const key = itemId ? `${branch}:${itemId}` : branch;
    this.queue.set(key, { branch, itemId, read, retries: 0 });
    const prev = this.timers.get(key);
    if (prev) clearTimeout(prev);
    this.timers.set(
      key,
      setTimeout(() => {
        this.timers.delete(key);
        const item = this.queue.get(key);
        if (!item) return;
        this.queue.delete(key);
        void this.dispatch(item, key);
      }, DEBOUNCE_MS),
    );
  }

  private seedTrackerFromLastModified(lastModified: string | null): void {
    if (!lastModified) return;
    for (const branch of ALL_BRANCHES) {
      this.branchTimestamps.set(branch, lastModified);
    }
  }

  private emitConflict(kind: SyncConflictKind): void {
    const now = Date.now();
    if (kind === 'offline') {
      if (now - this.lastOfflineToastAt < OFFLINE_TOAST_COOLDOWN_MS) return;
      this.lastOfflineToastAt = now;
    }
    if (kind === 'auto-resolved') {
      if (now - this.lastAutoResolvedToastAt < AUTO_RESOLVED_TOAST_COOLDOWN_MS) return;
      this.lastAutoResolvedToastAt = now;
    }
    this.events?.onConflict(kind);
  }

  private dispatch(item: SyncQueueItem, key: string): Promise<void> {
    // Serialize ALL dispatches globally: chain onto the previous one so only a
    // single PATCH is ever in flight for this user's row. The server resolves
    // each write against fresh state, so sequential dispatch means the CAS
    // always matches first try — no contention, no retry stampede.
    const run = this.inFlightChain.then(() => this.runDispatch(item, key));
    // Keep the chain alive even if an individual dispatch rejects.
    this.inFlightChain = run.catch(() => {});
    return run;
  }

  private async runDispatch(item: SyncQueueItem, _key: string): Promise<void> {
    // Silent recovery loop · NEVER surfaces a conflict toast (Genie hard rule).
    // On 412: refetch latest version → merge our pending writes over server state
    // (optimistic UI survives) → retry. Repeat up to MAX_CONFLICT_RETRIES; the
    // write lands once the concurrent session settles. Only genuine networkError
    // informs the user ('offline'); everything else keeps local + stays quiet.
    let current = item;
    for (;;) {
      try {
        await this.fire(current);
        return; // success — server has our write
      } catch (err) {
        if (err instanceof SyncConflictError) {
          if (current.retries >= MAX_CONFLICT_RETRIES) {
            // Budget exhausted (pathological churn). Keep the optimistic local
            // state; the next mutation or initialFetch re-syncs. No toast.
            console.warn('[progress-sync] conflict retries exhausted (kept local)', current.branch);
            return;
          }
          try {
            const envelope = await apiFetchEnvelope(ProgressGetResponse, '/api/progress');
            this.seedTrackerFromLastModified(envelope.meta.lastModified);
            // CRITICAL: do NOT apply server state to React on a conflict refetch.
            // The refetch exists only to refresh the version tracker for the retry.
            // Applying server state would clobber THIS tab's unsynced optimistic
            // edits — e.g. a tutorial dismiss that hasn't landed yet — so the
            // dismissed «Карта студии» would re-appear on the next navigation
            // (every nav writes the `last` branch → 412 → refetch → reset). Local
            // optimistic state stays authoritative; the retry below writes it
            // through. Cross-tab pull happens on next page load (initialFetch).
            // 21 JUN · Genie «туториал лезет на каждом окне» regression.
          } catch (refetchErr) {
            if (refetchErr instanceof ApiClientError && refetchErr.code === 'networkError') {
              this.emitConflict('offline'); // genuine connectivity — keep informing
              return;
            }
            // Transient refetch failure — fall through to a backoff + retry.
          }
          current = { ...current, retries: current.retries + 1 };
          await this.delay(Math.min(CONFLICT_BACKOFF_MS * current.retries, CONFLICT_BACKOFF_CAP_MS));
          continue;
        } else if (err instanceof ApiClientError && err.code === 'networkError') {
          this.emitConflict('offline');
          return;
        } else {
          // Unknown / non-412 server error · keep local optimistic state, stay
          // silent (no scary toast). Logged for diagnostics; next edit re-syncs.
          console.warn('[progress-sync] dispatch failure (kept local)', err);
          return;
        }
      }
    }
  }

  /** Resolves after `ms` (chain-blocking by design — serializes the retry). */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Storm detector (P4). Record a sent PATCH; if the rolling-minute rate crosses
   * the threshold, emit ONE `analytics_events` row (count + branch mix) and cool
   * down. Fire-and-forget · consent-gated inside `trackSyncStorm` (no analytics
   * user → dropped) · never toasts, never throws into the sync path.
   */
  private recordPatchForDetector(branch: BranchKey): void {
    const now = Date.now();
    this.patchWindow.push({ at: now, branch });
    // Prune outside the rolling window.
    const cutoff = now - STORM_WINDOW_MS;
    while (this.patchWindow.length > 0 && this.patchWindow[0]!.at < cutoff) {
      this.patchWindow.shift();
    }
    if (
      this.patchWindow.length > STORM_PATCH_THRESHOLD &&
      now - this.lastStormReportAt > STORM_REPORT_COOLDOWN_MS
    ) {
      this.lastStormReportAt = now;
      const branches = [...new Set(this.patchWindow.map((e) => e.branch))];
      try {
        trackSyncStorm(this.patchWindow.length, branches);
      } catch {
        /* observability must never affect sync */
      }
    }
  }

  /**
   * Dispatch the actual PATCH for a queued item. Reads the per-branch
   * `If-Unmodified-Since` tracker, sends the payload, and on 2xx updates
   * the tracker from `meta.lastModified` (server-truth).
   *
   * Throws `SyncConflictError` on 412 · `ApiClientError(networkError)` on
   * offline · standard errors otherwise — caller in `runDispatch` decides
   * how to react (retry once vs persistent vs offline).
   */
  private async fire(item: SyncQueueItem): Promise<void> {
    // Defence-in-depth (multi-tab account swap): refuse to write this session's
    // data under a different account's token. Only a CONCRETE mismatch aborts —
    // a null/absent session (e.g. mid-refresh) falls through to the normal auth
    // path so a transient blip never drops a legitimate write.
    if (this.ownerId) {
      const { data } = await supabase.auth.getSession();
      const sessionUserId = data.session?.user?.id ?? null;
      if (sessionUserId && sessionUserId !== this.ownerId) {
        // Session belongs to another account now — drop the queue, stop writing.
        this.setEnabled(false);
        return;
      }
    }
    const route = BRANCH_ROUTES[item.branch];
    const path = route.buildPath(item.itemId);
    if (!path) {
      // Per-id branch без itemId · drop silently (likely race · UI already
      // wrote the parent branch). Tracker unchanged.
      return;
    }
    // Read the LIVE value at SEND time (ADR-019 · P2). A retry re-reads here, so
    // it always ships the current value — never the snapshot captured at
    // schedule time. A per-id key that vanished between schedule and dispatch
    // reads `undefined` → skip (never PATCH an undefined body).
    const payload = item.read();
    if (payload === undefined) return;
    this.recordPatchForDetector(item.branch);
    // First PATCH per branch sends NO `If-Unmodified-Since`. Subsequent PATCHes
    // send the value captured from the previous response's `Last-Modified`.
    // This avoids spurious 412s on the cold start path (tracker not yet seeded
    // from PATCH response · initialFetch seeds GET timestamps which are
    // semantically different from PATCH's row-write timestamp).
    const branchUpdatedAt = this.branchPatched.has(item.branch)
      ? this.branchTimestamps.get(item.branch)
      : undefined;
    const envelope = await apiFetchEnvelope(PatchEchoPassthrough, path, {
      method: 'PATCH',
      body: payload,
      branchUpdatedAt,
    });
    if (envelope.meta.lastModified) {
      this.branchTimestamps.set(item.branch, envelope.meta.lastModified);
    }
    this.branchPatched.add(item.branch);
  }
}

/** Module singleton · prod consumer. */
export const progressSync = new ProgressSyncManager();

/**
 * Test-only re-export of the class so unit tests can spin up a fresh
 * instance per test (`new __ProgressSyncManagerForTests()`) without
 * leaking state across cases. Don't import from production code.
 */
export const __ProgressSyncManagerForTests = ProgressSyncManager;

export type { BranchKey, SyncQueueItem };
